import { memo } from 'react';
import { Flex } from '@chakra-ui/react';
import { useSelector } from 'react-redux';

import { getBlockMetadata } from 'redux/PlanPageSlice';

import {
  PLAN_HEADER_HORIZONTAL_PADDING,
  PLAN_TOOLBAR_ARROW_TAB_WIDTH,
  PLAN_TOOLBAR_HEIGHT,
  accent,
  neutral,
  text as textToken,
} from '../planPageRevampUi';

import PlanFilterGroupPill from './PlanFilterGroupPill';
import PlanToolbarArrowTabButton from './PlanToolbarArrowTabButton';
import PlanViewModeTabs from './PlanViewModeTabs';

export type PlanViewMode = 'chart-table' | 'chart' | 'table';

interface Properties {
  showInputsPanel: boolean;
  onToggleInputsPanel: () => void;
  showWhatIfsPanel: boolean;
  onToggleWhatIfsPanel: () => void;
  hasWhatIfsDrivers: boolean;
  showAnalytics?: boolean;
  viewMode: PlanViewMode;
  onViewModeChange: (mode: PlanViewMode) => void;
  refetchRequest: () => void;
}

const PlanToolbar = ({
  showInputsPanel,
  onToggleInputsPanel,
  showWhatIfsPanel,
  onToggleWhatIfsPanel,
  hasWhatIfsDrivers,
  showAnalytics = true,
  viewMode,
  onViewModeChange,
  refetchRequest,
}: Properties) => {
  const metadata = useSelector(getBlockMetadata);
  const hasDimensionFilters = (metadata?.dimensions?.length ?? 0) > 0;

  return (
    // Figma 76685:11608 — the toolbar row draws a `border-t` (a divider
    // against the title row above it, both inside the same white header
    // strip). No `bg`/`borderBottom` of its own: the parent header Box in
    // `PlanPageContent.tsx` already paints the strip white and closes it with
    // one bottom border — this row painting the same things again was what
    // doubled that edge into two 1px lines the last time this drifted.
    <Flex
      w='100%'
      px={PLAN_HEADER_HORIZONTAL_PADDING}
      h={PLAN_TOOLBAR_HEIGHT}
      alignItems='center'
      gap={3}
      borderTop='1px solid'
      borderColor={neutral.border}
    >
      <Flex alignItems='center' gap={3} minW={0} flexShrink={0}>
        <PlanToolbarArrowTabButton
          label='View Inputs'
          direction='left'
          width={PLAN_TOOLBAR_ARROW_TAB_WIDTH}
          isActive={showInputsPanel}
          // Shared revamp tokens, not the plan page's own ThemeSlice palette.
          // The two disagree on every chrome value — #D1D6DD vs #D9D9D9 for a
          // border, #1F2430 vs #121826 for text — so with the title bar and KPI
          // row already moved across, this strip was rendering a second, very
          // slightly different grey directly beneath them.
          activeBg={accent.subtle}
          activeColor={accent.solid}
          borderColor={neutral.border}
          textColor={textToken.primary}
          onClick={onToggleInputsPanel}
        />

        {hasDimensionFilters && <PlanFilterGroupPill refetchRequest={refetchRequest} />}
      </Flex>

      <Flex alignItems='stretch' gap={3} ml='auto' flexShrink={0} h='100%'>
        {showAnalytics && (
          <PlanViewModeTabs
            viewMode={viewMode}
            onChange={onViewModeChange}
            primaryColor={accent.solid}
            textColor={textToken.primary}
            activeBg={accent.subtle}
          />
        )}

        {showAnalytics && hasWhatIfsDrivers && (
          <Flex alignItems='center' flexShrink={0}>
            <PlanToolbarArrowTabButton
              label='What Ifs'
              direction='right'
              width={PLAN_TOOLBAR_ARROW_TAB_WIDTH}
              isActive={showWhatIfsPanel}
              activeBg={accent.subtle}
              activeColor={accent.solid}
              borderColor={neutral.border}
              textColor={textToken.primary}
              onClick={onToggleWhatIfsPanel}
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default memo(PlanToolbar);
